import Vue from "vue";
import { query } from "@sinao/core-js";
import { reformatIsoStringToDate, reformatDateToIsoString } from "@/mixins/methods";
import i18n from "@/i18n";

const recurring = {
	state: () => ({
		// UI
		isRecurringInvoice: false,
		enableRecurringDiscount: false,
		enableOrdersPlan: false,
		// API
		nextInvoiceAt: null,
		endAt: null,
		frequencyCount: 1,
		frequencyDuration: "month",
		discount: 0,
		discountMode: "percent",
		discountStartAt: null,
		discountEndAt: null,
		ordersPlan: [],
		savingStatus: "draft",
		emailInvoice: false,
		notAttachInvoice: true,
		periods: {},
		period: "none_",
		recipients: {
			main: "", // email "à l'attention de"
			copyBcc: 0, // send a copy to the administrator email
			emails: [] // list of the emails tags currently displayed
		}
	}),
	getters: {
		isRecurringReady(state) {
			if (!state.ordersPlan)
				return true;

			const _ordersPlan = state.ordersPlan.filter(plan => plan.model);
			if (state.isRecurringInvoice === false)
				return true;
			if (_ordersPlan.length === 0) {
				return true;
			}
			else {
				return !_ordersPlan[_ordersPlan.length - 1].end_at?.length;
			}
		}
	},
	mutations: {
		isRecurringInvoice(state, payload) {
			state.isRecurringInvoice = payload;
		},
		enableRecurringDiscount(state, payload) {
			state.enableRecurringDiscount = payload;
		},
		enableOrdersPlan(state, payload) {
			state.enableOrdersPlan = payload;
		},
		nextInvoiceAt(state, payload) {
			state.nextInvoiceAt = payload;
		},
		endAt(state, payload) {
			state.endAt = payload;
		},
		frequencyCount(state, payload) {
			state.frequencyCount = payload;
		},
		frequencyDuration(state, payload) {
			state.frequencyDuration = payload;
		},
		discount(state, payload) {
			state.discount = payload;
		},
		discountMode(state, payload) {
			state.discountMode = payload;
		},
		discountStartAt(state, payload) {
			state.discountStartAt = payload;
		},
		discountEndAt(state, payload) {
			state.discountEndAt = payload;
		},
		ordersPlan(state, payload) {
			state.ordersPlan = payload ?? [];
		},
		savingStatus(state, payload) {
			state.savingStatus = payload;
		},
		emailInvoice(state, payload) {
			state.emailInvoice = payload;
		},
		notAttachInvoice(state, payload) {
			state.notAttachInvoice = payload;
		},
		periods(state, payload) {
			state.periods = payload;
		},
		period(state, payload) {
			state.period = payload;
		},
		updateRecipientProperty(state, payload) {
			state.recipients[payload.property] = payload.value
		},
		initRecipientEmailsFromContactInfosEstablishments(state, establishments) {
			if (establishments) {
				state.recipients.emails =  establishments.flatMap(establishment => {
					return establishment.contacts.filter(contact => contact.email && contact.email !== state.recipients.main).map(contact => contact.email);
				});
			} else {
				state.recipients.emails = [];
			}
		},
		resetAllRecipientsProperties(state) {
			state.recipients = Object.assign({}, {
				recorded: [],
				main: "",
				copyBcc: 0,
				emails: []
			});
		}
	},
	actions: {
		async loadRecurringDocument({ state, commit, dispatch, rootGetters, rootState }) {
			const { app } = rootState;
			const response = await query({
				operationId: "app.documents.sales.recurringinvoices.get",
				params: {
					appId: app.id,
					id: app.documentId,
					"expand[0]": "customer.establishments",
					"expand[1]": "customer.relationship",
					'expand[2]': 'customer.establishments.contacts',
				}
			});
			// const { contact_infos, content, bank_details_id, details } = response;

			// Case 1 : No order plan, we show the content
			if (response?.content) {
				dispatch("loadDocument", {
					data: {
						/*
						details,
						contact_infos,
						content,
						bank_details_id,
						*/
						...response
					},
					force: true
				});
			} else {
				dispatch("loadDocument", {
					data: {
						// details,
						// contact_infos,
						// bank_details_id
						...response
					},
					force: true
				});

				if (response?.orders_plan?.length > 0) {
					const { model } = response.orders_plan.slice(-1)[0];

					if (!model) {
						return;
					}

					// Load the model by id
					const selectedModel = rootGetters.getModelById(model);

					commit("selectedModel", selectedModel);
					dispatch("loadModel", selectedModel?.id);

					// Case 2 : Selected a model, we have one entry on the order plan with a model id, ie { model: id, end_at: null }
					// if (response?.orders_plan.length === 1);
					// Case 3 : Order plan enabled, we have multiple model ids, we show the last one
					// else if (response?.orders_plan > 1);

					commit("enableOrdersPlan", true);
				}
			}

			if (response?.discount) {
				commit("enableRecurringDiscount", true);
			}

			if (response?.orders_plan) {
				state.ordersPlan = response.orders_plan.map(plan => {
					return {
						...plan,
						end_at: plan?.end_at ? reformatIsoStringToDate(plan?.end_at) : null
					};
				});
			} else {
				state.ordersPlan = [];
			}

			// Load data to the interface
			Vue.nextTick(() => {
				commit("title", response?.title);
			});

			commit("frequencyCount", response?.frequency_count ?? 1);
			commit("frequencyDuration", response?.frequency_duration ?? "month");
			commit("nextInvoiceAt", response?.next_invoice_at ? reformatIsoStringToDate(response?.next_invoice_at) : null) ;
			commit("endAt", response?.end_at ? reformatIsoStringToDate(response?.end_at) : null);
			commit("discount", (response?.discount_mode == "percent") ? response?.discount  : response?.discount / 100);
			commit("discountMode", response?.discount_mode ?? null);
			commit("discountStartAt", response?.discount_start_at
				? reformatIsoStringToDate(response?.discount_start_at)
				: null);
			commit("discountEndAt", response?.discount_end_at ? reformatIsoStringToDate(response?.discount_end_at) : null);
			commit("savingStatus", response?.saving_status);
			commit("emailInvoice", response?.email_invoice ? true : false);
			commit("notAttachInvoice", response?.attach_invoice ? false : true);
			// recipient emails
			commit("updateRecipientProperty", {
				property: "copyBcc",
				value: response?.need_copy_bcc ? response.need_copy_bcc : 0
			});
			// Retro Compatibility
			if (!response?.recipient_emails?.length && !response?.customer_email?.length) { // old way befaore recipient_emails implementation
				commit("updateRecipientProperty", {
					property: "emails",
					value: response?.emails ? response.emails : []
				});
				commit("updateRecipientProperty", {
					property: "main",
					value: ""
				});
			} else { // new way
				commit("updateRecipientProperty", {
					property: "emails",
					value: response?.recipient_emails ? response.recipient_emails : []
				});
				commit("updateRecipientProperty", {
					property: "main",
					value: response?.customer_email ? response.customer_email : ""
				});
			}


			return response;
		},
		async buildRecurringDocument({ dispatch, state, rootState, commit, rootGetters }) {
			const { models, settings } = rootState;

			const document = await dispatch("buildDocument");

			if (!document) {
				return;
			}

			commit('ordersPlan',  state.ordersPlan.filter(plan => plan.model));

			const {
				nextInvoiceAt,
				endAt,
				frequencyCount,
				frequencyDuration,
				discount,
				discountMode,
				discountStartAt,
				discountEndAt,
				ordersPlan,
				savingStatus,
				emailInvoice,
				notAttachInvoice,
				recipients
			} = state;

			const selectedModel = models.selectedModel ? models.selectedModel[0] : null;



			// If no order plan OR model is provided
			if (ordersPlan && ordersPlan.length === 0 && !selectedModel) {
				state.ordersPlan = null;
			} else {
				if (document?.content) {
					document.content = null;
				}

				// If a model is selected
				if (selectedModel?.id) {
					state.ordersPlan = {
						id: selectedModel.id,
						end_at: null
					};
				}
			}

			const _nextInvoiceAt = reformatDateToIsoString(nextInvoiceAt);
			const _endAt = reformatDateToIsoString(endAt);
			const _discountStartAt = reformatDateToIsoString(discountStartAt);
			const _discountEndAt = reformatDateToIsoString(discountEndAt);

			const _contactInfos = Object.assign({}, rootState.contacts.contactInfos);
			// prevent "null" in string
			if (_contactInfos.details && (!_contactInfos.details.length || _contactInfos.details.toLowerCase() === "null"))
				_contactInfos.details = null;

			if (_contactInfos.email && (!_contactInfos.email.length || _contactInfos.email.toLowerCase() === "null"))
				_contactInfos.email = null;

			if (_contactInfos.location && (!_contactInfos.location.length || _contactInfos.location.toLowerCase() === "null"))
				_contactInfos.location = null;

			const is_foreign_currency = (settings.currency?.iso?.length && settings.config.currency !== settings.currency.iso);

			const data = {
				...document,
				next_invoice_at: _nextInvoiceAt,
				end_at: _endAt,
				frequency_count: frequencyCount,
				frequency_duration: frequencyDuration,
				discount: (discountMode == "percent") ? parseInt(discount) : parseInt(discount) * 100,
				discount_mode: discountMode,
				discount_start_at: _discountStartAt,
				discount_end_at: _discountEndAt,
				orders_plan: ordersPlan,
				saving_status: savingStatus,
				email_invoice: emailInvoice,
				attach_invoice: !notAttachInvoice,
				recipient_emails: recipients.emails,
				need_copy_bcc: recipients.copyBcc,
				contact_infos: {
					..._contactInfos,
				},
				foreign_currency: is_foreign_currency
					? settings.currency.iso
					: null,
				// we do not send amount as it is calculated by API
				// expect amount_net_foreign_currency in case of foreign currency
				amount_net_foreign_currency: is_foreign_currency ? rootGetters.totals.table.inclTax * 100 : null
			};
			return data;
		},
		async getPeriods({ state, commit, rootState }) {
			const { app } = rootState;
			commit("savingDocument", true);

			const response = await query({
				operationId: "app.documents.sales.recurringinvoices.getPeriods",
				params: {
					appId: app.id,
					date: state.nextInvoiceAt
				}
			});
			if (response) {
				commit("periods", response);
			}

			commit("savingDocument", false);
		},
		checkForRecurringPeriodVarInDetails({ commit }, payload) {
			const matches = payload.match(/\{[a-z-_]{2,}-period\}/);
			if (matches && matches.length >= 1) {
				commit('period', matches[0].substring(1, (matches[0].length - 1)));
			}
		}
	}
};

export default recurring;
